import api from './api';
import { clearChatStore } from './chatStore';
import { notify } from './attention';

/**
 * Keeping this browser signed in, and signing it out cleanly.
 *
 * - The sign-in lasts 14 days without use (server/services/authSessions.js).
 *   While the site is used it asks for a fresh token about once a day, so
 *   someone who uses it regularly is never signed out.
 * - A token that has run out is noticed before the page draws anything
 *   (settleStoredSession, from index.js), so the person lands on the login page
 *   instead of a page full of "could not be loaded" errors.
 * - When the server says the session has ended (a 401 from utils/api.js, or the
 *   socket's session:ended), endWebSession signs out at once and tells them why
 *   on the login page, with a desktop notification if the tab is in the background.
 */

const REASON_KEY = 'session_end_reason';
const RENEW_AFTER_MS = 24 * 60 * 60 * 1000;
const RENEW_RETRY_MS = 10 * 60 * 1000;
// Login tokens from before sessions lasted 24 hours, view-as tokens 2: only the first can be renewed.
const LEGACY_LOGIN_MIN_LIFETIME_S = 20 * 60 * 60;

/** The claims inside a token, or null if it is not one. Reads only; checking it is the server's job. */
export const tokenClaims = (token) => {
    try {
        const part = String(token).split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(atob(part.padEnd(part.length + ((4 - (part.length % 4)) % 4), '=')));
    } catch {
        return null;
    }
};

export const tokenExpired = (token, now = Date.now()) => {
    const claims = tokenClaims(token);
    return !claims || (Boolean(claims.exp) && claims.exp * 1000 <= now);
};

const MESSAGES = {
    expired: 'Your session ran out. Sign in again to carry on.',
    password_changed: 'Your password was changed, so you were signed out. Sign in with the new password.',
    deactivated: 'Your account has been deactivated. Contact HR if this is a mistake.'
};
/** What the login page says about why you are there, or null. */
export const messageForReason = (reason) => {
    if (!reason || reason === 'signed_out') return null;
    return MESSAGES[reason] || 'You were signed out. Sign in again to carry on.';
};

/** The reason the last session ended, once: the login page reads it and it is gone. */
export const takeSessionEndReason = () => {
    try {
        const reason = sessionStorage.getItem(REASON_KEY);
        sessionStorage.removeItem(REASON_KEY);
        return reason;
    } catch {
        return null;
    }
};

const rememberReason = (reason) => {
    try { sessionStorage.setItem(REASON_KEY, reason); } catch { /* storage unavailable */ }
};

/**
 * A view-as session (an admin seeing the portal as someone else) that ran out
 * goes back to the admin's own sign-in, if that is still good, rather than
 * signing the admin out. Returns true when it did.
 */
const returnToAdmin = () => {
    if (localStorage.getItem('is_impersonating') !== 'true') return false;
    const adminToken = localStorage.getItem('admin_token_backup');
    const adminUser = localStorage.getItem('admin_user_backup');
    localStorage.removeItem('admin_token_backup');
    localStorage.removeItem('admin_user_backup');
    localStorage.removeItem('is_impersonating');
    if (!adminToken || !adminUser || tokenExpired(adminToken)) return false;
    localStorage.setItem('token', adminToken);
    localStorage.setItem('user', adminUser);
    return true;
};

const clearSignIn = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    clearChatStore();
};

/**
 * Before the app is drawn: a stored sign-in that has already run out is
 * cleared, so every protected page sends the person to the login page.
 */
export const settleStoredSession = () => {
    const token = localStorage.getItem('token');
    if (!token || !tokenExpired(token)) return;
    if (returnToAdmin()) return;
    clearSignIn();
    rememberReason('expired');
};

let ending = false;

/** The session has ended: sign out of this tab now and go to the login page. */
export const endWebSession = (reason = 'ended') => {
    if (ending) return;
    ending = true;
    window.dispatchEvent(new Event('gts:session-ending'));

    if (returnToAdmin()) {
        window.location.href = '/employees';
        return;
    }
    clearSignIn();
    rememberReason(reason);

    const toLogin = () => { window.location.href = '/login'; };
    // In the background: a desktop notification (if allowed), and the login page when they come back.
    const message = messageForReason(reason);
    if (document.hidden && message) {
        notify({ title: 'You have been signed out', body: message, tag: 'session-ended' });
        const onVisible = () => {
            if (document.hidden) return;
            document.removeEventListener('visibilitychange', onVisible);
            toLogin();
        };
        document.addEventListener('visibilitychange', onVisible);
        return;
    }
    toLogin();
};

let renewing = false;
let lastRenewTry = 0;

/** About once a day while the site is used: a fresh token, so the 14 days start again. */
export const renewIfDue = async () => {
    const token = localStorage.getItem('token');
    if (!token || ending || renewing) return;
    if (localStorage.getItem('is_impersonating') === 'true') return;
    const claims = tokenClaims(token);
    if (!claims?.iat) return;
    if (claims.sid && Date.now() - claims.iat * 1000 < RENEW_AFTER_MS) return;
    if (!claims.sid && (!claims.exp || claims.exp - claims.iat < LEGACY_LOGIN_MIN_LIFETIME_S)) return;
    if (Date.now() - lastRenewTry < RENEW_RETRY_MS) return;
    lastRenewTry = Date.now();
    renewing = true;
    try {
        const res = await api.post('/auth/renew');
        // Only if nothing else changed the sign-in meanwhile (another tab, view-as).
        if (res.data?.token && localStorage.getItem('token') === token) localStorage.setItem('token', res.data.token);
    } catch {
        // Offline or the server busy: tried again later. A 401 is handled by utils/api.js.
    } finally {
        renewing = false;
    }
};

let watching = false;

/** From index.js, once: keep an eye on the sign-in for as long as the tab is open. */
export const startSessionWatch = () => {
    if (watching) return;
    watching = true;
    const signedInAtStart = Boolean(localStorage.getItem('token'));

    const check = () => {
        const token = localStorage.getItem('token');
        if (!token || ending) return;
        if (tokenExpired(token)) endWebSession('expired');
        else renewIfDue();
    };
    check();
    setInterval(check, 60 * 1000);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) check(); });

    // The server closed this session's socket (utils/socket.js passes it on).
    window.addEventListener('gts:session-ended', (event) => endWebSession(event.detail?.reason || 'ended'));

    // Signed out in another tab of this browser: follow it to the login page.
    window.addEventListener('storage', (event) => {
        if (event.key !== 'token' || event.newValue || ending || !signedInAtStart) return;
        ending = true;
        window.location.href = '/login';
    });
};
